/**
 * DEFAULT_CONTENT_BUNDLE v1: the declarative business content the server seed publishes and the
 * desktop activates. Bodies follow domain/templates.ts; items are content-addressed with
 * makeBundleItem and the manifest digest uses computeBundleDigest. The manifest is unsigned here
 * and signed by the server's content key at publish time.
 */
import type { ContentBundle, ContentBundleItem } from '../api/content.js';
import type { ContentManifest, TemplateType } from '../api/entities.js';
import type { UserId } from '../ids.js';
import { canonicalJson } from '../domain/hash.js';
import {
  computeBundleDigest,
  makeBundleItem,
  type AgendaTemplateBody,
  type EmailTemplateBody,
  type FeatureFlagsBody,
  type MatchingRuleBody,
  type ModelConfigBody,
  type RateLimitConfigBody,
  type ReportColumnsBody,
} from '../domain/templates.js';
import { TASK_GROUP_CATEGORIES, type TaskGroupCategory } from '../providers/records.js';
import { CLASSIFICATION_RULE_BODIES, REMEDIATION_ASK_BODIES } from './asks.js';
import { ALL_HELP_ARTICLES, helpArticleBody, helpTemplateId } from './help.js';
import { FIXTURE_NOW_ISO } from './prng.js';
import { staffBySlug } from './staff.js';

export const DEFAULT_CONTENT_VERSION = '1.2.0';
export const DEFAULT_CONTENT_CHANGE_SUMMARY = 'Agenda layout from the analysts\' corrected agenda: serif font, centred header, full-width rule and section spacing (template feedback 2026-09-29).';
export const DEFAULT_CONTENT_CHANNEL = 'stable' as const;
export const DEFAULT_CONTENT_COMPATIBLE_APP_VERSIONS = '>=1.0.0-local <2.0.0';
export const CONTENT_SCHEMA_VERSION = 1;
export const TEMPLATE_VARIABLE_SCHEMA_VERSION = 1;
/** Agenda and email templates use the v2 variable schema (template feedback 2026-09-25). */
export const AGENDA_VARIABLE_SCHEMA_VERSION = 2;
export const EMAIL_VARIABLE_SCHEMA_VERSION = 2;

// ---------------------------------------------------------------------------------------------
// Agenda template (Dakota's rolling agenda, template feedback 2026-09-25)
// ---------------------------------------------------------------------------------------------

/** The underscore rule under the header block, as in the template agenda. */
export const AGENDA_SEPARATOR = '_'.repeat(83);

/**
 * Dakota's "Bi-Weekly Status Call Rolling Meeting Agenda": bold header block, "Monday, August
 * 10th, 2026 | Asynchronous Update", bold emoji headings, "- N/A" placeholders in operator
 * sections, every ask as "- {ask}" with each child as a nested bullet, italic group labels, the
 * "Please work through …" line pointing at the linked sheets, Controls and Evidence per
 * framework, the sprint history on its own tab and Overall capped at 15 with the rest on the
 * Additional asks tab.
 */
export const DEFAULT_AGENDA_TEMPLATE: AgendaTemplateBody = {
  variablesSchemaVersion: AGENDA_VARIABLE_SCHEMA_VERSION,
  title: '[{account_name}] vCISO Rolling Agenda',
  headerLines: ['{account_name} & Agency', 'Bi-Weekly Status Call Rolling Meeting Agenda'],
  separator: AGENDA_SEPARATOR,
  meetingLine: '{meeting_date_long} | Asynchronous Update',
  headingStyle: 'bold',
  // Dakota's corrected agenda (template feedback 2026-09-29): serif font, centred header and meeting
  // line, a full-width rule instead of underscores, a blank line's worth of space before each section.
  layout: { fontFamily: 'Times New Roman', centerHeader: true, separatorAsRule: true, sectionSpacingPt: 12 },
  sections: [
    { id: 'advisories', heading: '⚠️ Advisories:', managed: false, defaultMarkdown: '- N/A', tab: 'agenda', intro: null },
    { id: 'agenda_items', heading: '💬 Agenda:', managed: false, defaultMarkdown: '- N/A', tab: 'agenda', intro: null },
    { id: 'current_blockers', heading: '🚧 Current Blockers:', managed: false, defaultMarkdown: '- N/A', tab: 'agenda', intro: null },
    { id: 'customer_asks', heading: '❓ {account_name} Asks:', managed: true, defaultMarkdown: '', tab: 'agenda', intro: 'Tasks we need {account_name} to complete.' },
    { id: 'framework_status', heading: '〽️ Status Per Framework:', managed: true, defaultMarkdown: '', tab: 'agenda', intro: null },
    { id: 'relevant_links', heading: '💾 Relevant Links:', managed: false, defaultMarkdown: '- N/A', tab: 'agenda', intro: null },
    { id: 'completed_last_sprint', heading: 'Completed in Last Sprint:', managed: false, defaultMarkdown: '- N/A', tab: 'agenda', intro: null },
    { id: 'previously_completed', heading: 'Previously completed sprints', managed: false, defaultMarkdown: '**Sprint:**\n- N/A', tab: 'history', intro: null },
  ],
  askLineTemplate: '- {ask}',
  childrenStyle: 'nested',
  groupHeadings: { overall: 'Overall', infrastructure: 'Infrastructure', company_compliance: 'Company Compliance', employee_compliance: 'Employee Compliance' },
  groupLabelStyle: 'italic',
  groupLimits: { overall: 15 },
  overflowHeading: '❓ {account_name} Asks (continued):',
  overflowNote: 'The remaining {count} {group} asks are listed on the {tab} tab.',
  sheetsLine: 'Please work through {sheet_links}.',
  childLimit: null,
  emptyAsksText: 'No open asks in the selected snapshot.',
  missingAskFallback: { document: 'Please provide: {title}', policy: 'Please provide: {title}', default: '{title}' },
  providerRemediation: 'sanitized',
  tabTitles: { agenda: 'Agenda', history: 'Previously completed sprints', overflow: 'Additional asks' },
};

// ---------------------------------------------------------------------------------------------
// Email templates (spec section 10)
// ---------------------------------------------------------------------------------------------

export const EMAIL_TEMPLATE_MODES = ['group', 'personalized', 'internal_summary'] as const;
export type EmailTemplateMode = (typeof EMAIL_TEMPLATE_MODES)[number];

/**
 * Bundle body for outreach templates. The domain schema defines the first five fields; the
 * remaining flags are additive metadata for the outreach UI (mode, privacy flags, recommendation).
 */
export interface EmailTemplateFixtureBody extends EmailTemplateBody {
  mode: EmailTemplateMode;
  /** Internal summaries may list recipients; they are never sent to customer staff. */
  internalOnly: boolean;
  usesEmployeeList: boolean;
  /** Personalized mode is the recommended outreach mode. */
  recommended: boolean;
}

interface TaskGroupCopy {
  /** Short task name used in template names. */
  name: string;
  subject: string;
  /** The body sent to employees: Dakota's wording verbatim where a template exists (template feedback 2026-09-25). */
  body: string;
}

/** Dakota's opening line; {analyst_name} is the connected analyst's first name unless overridden in the compose pane. */
const OPENING = 'My name is {analyst_name} and I am a Security Analyst at Agency helping {company} with its compliance.';
const NOT_YOU = 'If you believe that you should not have received this email, please let me know!';

function dakotaBody(paragraphs: readonly string[], closing = 'Thank you.'): string {
  return ['Hi,', '', OPENING, '', ...paragraphs, '', closing].join('\n');
}

/**
 * Outreach bodies per task group. The five Dakota wrote (hard-disk encryption, password manager,
 * screen lock, Vanta Device Monitor, policies / training) are verbatim, with {company} for the
 * customer, {analyst_name} for the analyst and {onboarding_url} for the tenant onboarding page.
 * The others follow the same voice. Links stay bare URLs: the draft carries an HTML part that
 * makes them clickable.
 */
export const TASK_GROUP_COPY: Record<TaskGroupCategory, TaskGroupCopy> = {
  disk_encryption: {
    name: 'Hard-disk encryption',
    subject: '{company} compliance: hard-disk encryption on your computer',
    body: dakotaBody([
      'You currently do not have hard-disk encryption enabled on your computer. Full-disk encryption protects data stored on hard drives from unauthorized access if a device is lost or stolen.',
      '',
      `Please refer to the following link for instructions on how to do this. Your company may have a standard or suggested approach that it prefer employees use, so please verify that as well. ${NOT_YOU}`,
      '',
      'https://help.vanta.com/en/articles/11345839-encrypting-your-computer-hard-drives',
    ]),
  },
  password_manager: {
    name: 'Password manager',
    subject: '{company} compliance: password manager',
    body: dakotaBody([
      'You currently do not have a password manager configured or detected. Using a password manager is a fundamental security control that helps generate and securely store strong, unique passwords for each account. This significantly reduces the risk of password reuse, credential theft, and unauthorized access to company systems.',
      '',
      `Please refer to the following list of approved password managers tracked by Vanta. If you use one that is not on this list, please provide a screenshot with proof (including the time and date present on screen). Your company may have a preferred or required password management solution, so please verify that you are using the appropriate one. ${NOT_YOU}`,
      '',
      'https://help.vanta.com/en/articles/11346116-frequently-asked-questions-what-password-managers-does-vanta-detect',
    ]),
  },
  screenlock: {
    name: 'Screen lock',
    subject: '{company} compliance: screen lock on your computer',
    body: dakotaBody([
      'You currently do not have screen lock enabled on your computer. Screen lock is a fundamental endpoint security control that prevents unauthorized physical access to unattended workstations, protecting sensitive company data and reducing the risk of insider threats or opportunistic attacks.',
      '',
      `Please refer to the following links for instructions on how to enable screen lock. Your company may have a standard or suggested approach that it prefers employees use, so please verify that as well. ${NOT_YOU}`,
      '',
      'Mac:',
      'https://help.vanta.com/en/articles/11345842-configuring-screen-lock-on-macos-devices',
      '',
      'Windows:',
      'https://help.vanta.com/en/articles/11345843-configure-screen-lock-on-windows-devices',
    ]),
  },
  device_monitoring: {
    name: 'Vanta Device Monitor',
    subject: '{company} compliance: Vanta Device Monitor on your computer',
    body: [
      'Hi,',
      '',
      'My name is {analyst_name} and I am a Security Analyst at Agency helping {company} with their compliance.',
      '',
      'You currently do not have Vanta Device Monitor installed on your computer. The Vanta Device Monitor allows {company} and Agency to track important compliance requirements such as ensuring all employees have mandated hard-disk encryption enabled.',
      '',
      'Please refer to the following link for instructions on how to do this. If you believe you should not have received this email, please let me know!',
      '',
      'Mac:',
      'https://help.vanta.com/en/articles/11345397-installing-the-vanta-device-monitor-macos',
      '',
      'Windows:',
      'https://help.vanta.com/en/articles/11345396-installing-the-vanta-device-monitor-windows',
      '',
      'Thank you.',
    ].join('\n'),
  },
  policies_training: {
    name: 'Policies / training',
    subject: '{company} compliance: outstanding policies and training',
    body: [
      'Hi,',
      '',
      'My name is {analyst_name} and I am a Security Analyst at Agency helping out {company} with their compliance.',
      '',
      'You are receiving this email because you have at least 1 outstanding policy or training that you must accept or complete. I have the instructions below, but let me know if you have questions. Also happy to chat live about any questions you have here.',
      '',
      'Head to the Vanta Onboarding Page:',
      '{onboarding_url}',
      '• Click on "Accept Security Policies"',
      '• Please review the policies and accept them',
      '• Complete the listed trainings',
      '• Please watch the linked videos to the end',
      '• Click on the "Submit" button once finished',
      '',
      "Thanks! Don't hesitate to reach out!",
    ].join('\n'),
  },
  mfa: {
    name: 'Google Workspace MFA',
    subject: '{company} compliance: two-step verification on your Google account',
    body: dakotaBody([
      'You currently do not have two-step verification (MFA) enabled on your Google Workspace account. Multi-factor authentication is a fundamental security control: a stolen password alone no longer gives someone access to company email and files.',
      '',
      `Please refer to the following link for instructions on how to turn it on. Your company may have a standard or suggested approach that it prefers employees use, so please verify that as well. ${NOT_YOU}`,
      '',
      'https://support.google.com/accounts/answer/185839',
    ]),
  },
  antivirus: {
    name: 'Antivirus',
    subject: '{company} compliance: antivirus on your computer',
    body: dakotaBody([
      'You currently do not have antivirus (endpoint protection) detected on your computer. Antivirus is a fundamental endpoint security control that protects company data from malware on the devices used to access it.',
      '',
      `Please install the antivirus product your company has approved and make sure it is running and up to date. Your company may have a standard or suggested approach that it prefers employees use, so please verify that as well. ${NOT_YOU}`,
    ]),
  },
  background_check: {
    name: 'Background check',
    subject: '{company} compliance: background check',
    body: dakotaBody([
      'Our records show that your background check has not been completed. Background checks are a compliance requirement for {company}; the check is run by the provider your company uses, and you should have received a consent form from them.',
      '',
      `Please complete the consent form so the check can be processed, or let me know if you never received it. ${NOT_YOU}`,
    ]),
  },
  custom: {
    name: '{task_name}',
    subject: '{company} compliance: {task_name}',
    body: dakotaBody([
      'You are receiving this email because the task "{task_name}" is still open on your account. I have the instructions below, but let me know if you have questions.',
      '',
      `Please complete the task described in your compliance portal and mark it done. ${NOT_YOU}`,
    ]),
  },
  offboarding: {
    name: 'Offboarding access removal',
    subject: '{company} compliance: access removal for departed team members',
    body: dakotaBody([
      'Our records show that access for one or more departed team members has not been fully removed. Removing access when someone leaves is a compliance requirement for {company}.',
      '',
      'Please review the accounts listed in your compliance dashboard and remove or disable their access in the identity provider and connected applications, then let me know once this is done. This message is intended for the responsible administrator, not the departed person.',
    ]),
  },
};

function emailTemplate(category: TaskGroupCategory, mode: 'group' | 'personalized'): EmailTemplateFixtureBody {
  const copy = TASK_GROUP_COPY[category];
  return {
    variablesSchemaVersion: EMAIL_VARIABLE_SCHEMA_VERSION,
    name: `${copy.name === '{task_name}' ? 'Custom task' : copy.name} (${mode === 'group' ? 'group, Bcc' : 'personalized'})`,
    category,
    subject: copy.subject,
    body: copy.body,
    mode,
    internalOnly: false,
    usesEmployeeList: false,
    // Dakota's practice: one message to yourself with every employee in Bcc and the customer contact in Cc.
    recommended: mode === 'group',
  };
}

export const INTERNAL_SUMMARY_EMAIL_TEMPLATE: EmailTemplateFixtureBody = {
  variablesSchemaVersion: TEMPLATE_VARIABLE_SCHEMA_VERSION,
  name: 'Internal summary (lists recipients)',
  category: null,
  subject: 'Internal: {client_name} outreach summary for {task_name}',
  body: ['Internal summary. Do not forward to customer staff.', '', 'Account: {client_name}', 'Task: {task_name}', 'Recipients ({employee_count}):', '{employee_list}', '', 'Messages were prepared through Agency. Check Gmail for draft or send status.'].join('\n'),
  mode: 'internal_summary',
  internalOnly: true,
  usesEmployeeList: true,
  recommended: false,
};

export function emailTemplateId(category: TaskGroupCategory | null, mode: EmailTemplateMode): string {
  return mode === 'internal_summary' ? 'email.internal_summary' : `email.${category}.${mode}`;
}

export const DEFAULT_EMAIL_TEMPLATES: readonly { id: string; body: EmailTemplateFixtureBody }[] = [
  ...TASK_GROUP_CATEGORIES.flatMap((category) => [
    { id: emailTemplateId(category, 'group'), body: emailTemplate(category, 'group') },
    { id: emailTemplateId(category, 'personalized'), body: emailTemplate(category, 'personalized') },
  ]),
  { id: emailTemplateId(null, 'internal_summary'), body: INTERNAL_SUMMARY_EMAIL_TEMPLATE },
];

// ---------------------------------------------------------------------------------------------
// Matching rules (test/control name -> task group; versioned, regex without lookaround)
// ---------------------------------------------------------------------------------------------

export const DEFAULT_VANTA_MATCHING_RULES: MatchingRuleBody = {
  provider: 'vanta',
  version: 1,
  rules: [
    { id: 'vanta.policies_training.policies', taskGroupCategory: 'policies_training', namePattern: '^employees have accepted all policies', deviceTask: false },
    { id: 'vanta.policies_training.security', taskGroupCategory: 'policies_training', namePattern: '^employees have completed security awareness training', deviceTask: false },
    { id: 'vanta.policies_training.hipaa', taskGroupCategory: 'policies_training', namePattern: '^employees have completed hipaa training', deviceTask: false },
    { id: 'vanta.policies_training.ai', taskGroupCategory: 'policies_training', namePattern: '^employees have completed ai acceptable use training', deviceTask: false },
    { id: 'vanta.mfa.google', taskGroupCategory: 'mfa', namePattern: '^google workspace users have mfa enabled', deviceTask: false },
    { id: 'vanta.mfa.okta', taskGroupCategory: 'mfa', namePattern: '^okta users have mfa enrolled', deviceTask: false },
    { id: 'vanta.screenlock', taskGroupCategory: 'screenlock', namePattern: '^screen lock is enabled on employee computers', deviceTask: true },
    { id: 'vanta.disk_encryption', taskGroupCategory: 'disk_encryption', namePattern: '^hard disk encryption is enabled on employee computers', deviceTask: true },
    { id: 'vanta.antivirus', taskGroupCategory: 'antivirus', namePattern: '^antivirus is installed on employee computers', deviceTask: true },
    { id: 'vanta.password_manager', taskGroupCategory: 'password_manager', namePattern: '^password manager is installed on employee computers', deviceTask: true },
    { id: 'vanta.background_check', taskGroupCategory: 'background_check', namePattern: '^background checks are completed for employees', deviceTask: false },
    { id: 'vanta.device_monitoring.agent', taskGroupCategory: 'device_monitoring', namePattern: '^vanta agent is installed on employee computers', deviceTask: true },
    { id: 'vanta.device_monitoring.mdm', taskGroupCategory: 'device_monitoring', namePattern: '^employee computers are enrolled in device management', deviceTask: true },
    { id: 'vanta.device_monitoring.checkin', taskGroupCategory: 'device_monitoring', namePattern: '^employee computers checked in within', deviceTask: true },
    { id: 'vanta.offboarding', taskGroupCategory: 'offboarding', namePattern: '^access for offboarded employees is removed', deviceTask: false },
  ],
};

export const DEFAULT_DRATA_MATCHING_RULES: MatchingRuleBody = {
  provider: 'drata',
  version: 1,
  rules: [
    { id: 'drata.policies_training.policies', taskGroupCategory: 'policies_training', namePattern: '^policy acceptance', deviceTask: false },
    { id: 'drata.policies_training.security', taskGroupCategory: 'policies_training', namePattern: '^security awareness training', deviceTask: false },
    { id: 'drata.policies_training.hipaa', taskGroupCategory: 'policies_training', namePattern: '^hipaa training', deviceTask: false },
    { id: 'drata.mfa', taskGroupCategory: 'mfa', namePattern: '^mfa on identity provider', deviceTask: false },
    { id: 'drata.screenlock', taskGroupCategory: 'screenlock', namePattern: '^endpoint screen lock', deviceTask: false },
    { id: 'drata.disk_encryption', taskGroupCategory: 'disk_encryption', namePattern: '^endpoint disk encryption', deviceTask: false },
    { id: 'drata.antivirus', taskGroupCategory: 'antivirus', namePattern: '^endpoint antivirus', deviceTask: false },
    { id: 'drata.password_manager', taskGroupCategory: 'password_manager', namePattern: '^endpoint password manager', deviceTask: false },
    { id: 'drata.background_check', taskGroupCategory: 'background_check', namePattern: '^employee background checks', deviceTask: false },
    { id: 'drata.device_monitoring', taskGroupCategory: 'device_monitoring', namePattern: '^drata agent installed', deviceTask: false },
  ],
};

// ---------------------------------------------------------------------------------------------
// Report columns (spec sections 8 and 9; aligned with domain/sheets.ts headers)
// ---------------------------------------------------------------------------------------------

const col = (key: string, label: string, width: number, extra: { hidden?: boolean; wrap?: boolean } = {}) => ({ key, label, width, hidden: extra.hidden ?? false, wrap: extra.wrap ?? false });

export const DEFAULT_REPORT_COLUMNS: readonly ReportColumnsBody[] = [
  {
    report: 'infrastructure',
    columns: [col('platform', 'Platform', 160), col('task', 'Task', 520, { wrap: true }), col('status', 'Status', 150), col('notes', 'Notes and Comments', 360, { wrap: true }), col('sourceKey', 'Source Key', 120, { hidden: true })],
  },
  {
    report: 'employee_compliance',
    columns: [
      col('name', 'Name', 180),
      col('email', 'Email', 240),
      col('employment', 'Employment', 110),
      col('checks.policies', 'Policies', 100),
      col('checks.identity_mfa', 'Identity MFA', 100),
      col('checks.background_check', 'Background Check', 120),
      col('checks.agent_installed', 'Agent Installed', 110),
      col('checks.password_manager', 'Password Manager', 120),
      col('checks.disk_encryption', 'Disk Encryption', 110),
      col('checks.antivirus', 'Antivirus', 100),
      col('checks.auto_updates', 'Auto Updates', 100),
      col('checks.lock_screen', 'Lock Screen', 100),
      col('checks.security_training', 'Security Training', 120),
      col('checks.hipaa_training', 'HIPAA Training', 110),
      col('checks.ai_training', 'AI Training', 100),
      col('sourceTimestamp', 'Source Timestamp', 160),
      col('personKey', 'Person Key', 120, { hidden: true }),
    ],
  },
  // The Vulnerabilities sheet: the flat finding list first (the template vuln sheet's columns,
  // due dates as MM/DD/YYYY, Fixable as ✅ / ❌), then one row per package.
  {
    report: 'vulnerability_detail',
    columns: [
      col('source', 'Source', 120),
      col('asset', 'Asset', 240),
      col('assetId', 'Asset id', 260),
      col('identifier', 'Identifier', 150),
      col('packageName', 'Package name', 260),
      col('fixedVersion', 'Fixed version', 150),
      col('severity', 'Severity', 100),
      col('dueDate', 'Due date', 110),
      col('fixable', 'Fixable', 90),
      col('groupKey', 'Group Key', 120, { hidden: true }),
      col('findingKey', 'Finding Key', 120, { hidden: true }),
    ],
  },
  {
    report: 'vulnerability_summary',
    columns: [
      col('packageName', 'Package name', 220),
      col('packageIdentifier', 'Package identifier', 260),
      col('worstSeverity', 'Severity', 100),
      col('affectedAssetCount', 'Asset count', 100),
      col('findingCount', 'Finding count', 100),
      col('earliestDueDate', 'Earliest due date', 120),
      col('fixedVersion', 'Fixed version', 150),
      col('fixable', 'Fixable', 90),
      col('groupKey', 'Group Key', 220),
    ],
  },
  {
    report: 'sources',
    columns: [col('title', 'Title', 320, { wrap: true }), col('objectType', 'Type', 100), col('status', 'Status', 120), col('rawStatus', 'Raw Status', 120), col('frameworkIds', 'Frameworks', 160), col('timestamps.sourceUpdatedAt', 'Updated', 160), col('effectiveAsk.text', 'Ask', 420, { wrap: true }), col('recordKey', 'Record Key', 120, { hidden: true })],
  },
];

export function reportColumnsTemplateId(report: ReportColumnsBody['report']): string {
  return `reports.${report}`;
}

// ---------------------------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------------------------

export const DEFAULT_MODEL_CONFIG: ModelConfigBody = {
  defaultModel: 'gpt-6-luna',
  higherCapabilityModel: 'gpt-5',
  permittedModels: ['gpt-6-luna', 'gpt-5-mini', 'gpt-5'],
  maxOutputTokens: 4096,
  reasoningEffort: 'low',
  temperature: null,
  priceTable: {
    version: '2026-10-23',
    effectiveFrom: '2026-10-23T00:00:00.000Z',
    models: {
      // https://developers.openai.com/api/docs/models/gpt-6-luna — standard, short-context rates.
      'gpt-6-luna': { inputMicroUsdPerMillion: 100_000, cachedInputMicroUsdPerMillion: 10_000, outputMicroUsdPerMillion: 500_000 },
      'gpt-5-mini': { inputMicroUsdPerMillion: 250_000, cachedInputMicroUsdPerMillion: 25_000, outputMicroUsdPerMillion: 2_000_000 },
      'gpt-5': { inputMicroUsdPerMillion: 1_250_000, cachedInputMicroUsdPerMillion: 125_000, outputMicroUsdPerMillion: 10_000_000 },
    },
  },
  perUserBudgetMicroUsdPerMonth: 50_000_000,
  maxReservationMicroUsd: 500_000,
  baseUrl: 'https://api.openai.com/v1',
  policyVersion: 'ai-policy-2026-10',
};

export const DEFAULT_RATE_LIMIT_CONFIG: RateLimitConfigBody = {
  providers: {
    vanta: {
      // Use the full Manage API quota with steady pacing: a burst on top of 50/min
      // would exceed the documented minute limit before the first refill window ends.
      ceiling: { requestsPerMinute: 50, burst: 1, concurrency: 2 },
      auth: { requestsPerMinute: 5, burst: 1, concurrency: 1 },
      perEndpointFamily: {
        tests: { requestsPerMinute: 50, burst: 8, concurrency: 2 },
        test_entities: { requestsPerMinute: 50, burst: 6, concurrency: 2 },
        vulnerabilities: { requestsPerMinute: 50, burst: 6, concurrency: 1 },
        people: { requestsPerMinute: 50, burst: 4, concurrency: 1 },
      },
      egressGroupCeilingPerMinute: null,
    },
    drata: {
      ceiling: { requestsPerMinute: 200, burst: 20, concurrency: 4 },
      auth: { requestsPerMinute: 10, burst: 2, concurrency: 1 },
      perEndpointFamily: {
        monitor_failures: { requestsPerMinute: 120, burst: 12, concurrency: 2 },
        personnel: { requestsPerMinute: 60, burst: 6, concurrency: 1 },
      },
      egressGroupCeilingPerMinute: 400,
    },
  },
  retry: {
    maxRetries: 5,
    perRequestBudgetMs: 300_000,
    baseDelayMs: 1_000,
    maxDelayMs: 60_000,
    connectTimeoutMs: 10_000,
    responseTimeoutMs: 60_000,
  },
};

export const DEFAULT_FEATURE_FLAGS: FeatureFlagsBody = {
  flags: {
    rumi_enabled: true,
    rumi_full_review_enabled: true,
    rumi_local_embeddings_enabled: false,
    direct_send_enabled: true,
    outreach_group_mode_enabled: true,
    outreach_freshness_hours: 24,
    drata_outreach_enabled: true,
    vulnerability_include_unknown_fixability_drata: true,
    google_publish_enabled: true,
    prototype_import_enabled: true,
    support_bundle_enabled: true,
    stale_snapshot_days: 30,
    staging_retention_days: 7,
    storage_budget_warning_percent: 80,
    entitlement_renewal_lead_minutes: 10,
    release_channel: 'stable',
  },
  rumiDisabledAccountIds: [],
};

// ---------------------------------------------------------------------------------------------
// Bundle assembly
// ---------------------------------------------------------------------------------------------

export interface DefaultContentItemInput {
  type: TemplateType;
  id: string;
  name: string;
  description: string;
  body: unknown;
}

export const DEFAULT_CONTENT_ITEM_INPUTS: readonly DefaultContentItemInput[] = [
  { type: 'agenda_template', id: 'agenda.default', name: 'Default agenda', description: 'Bi-weekly rolling agenda in the Agency layout: managed asks with nested children, Controls and Evidence per framework, a sprint history tab and an Additional asks tab.', body: DEFAULT_AGENDA_TEMPLATE },
  ...DEFAULT_EMAIL_TEMPLATES.map((t) => ({ type: 'email_template' as const, id: t.id, name: t.body.name, description: `Outreach template (${t.body.mode}) for ${t.body.category ?? 'internal use'}.`, body: t.body })),
  ...REMEDIATION_ASK_BODIES.map((a) => ({ type: 'remediation_ask' as const, id: a.id, name: a.body.displayName, description: `Generic ask for ${a.body.canonicalKey}.`, body: a.body })),
  ...CLASSIFICATION_RULE_BODIES.map((r) => ({ type: 'classification_rule' as const, id: r.id, name: r.body.displayName, description: `Classification flags for ${r.body.canonicalKey}.`, body: r.body })),
  { type: 'matching_rule', id: 'matching.vanta', name: 'Vanta task-group matching', description: 'Maps Vanta test names to outreach task groups.', body: DEFAULT_VANTA_MATCHING_RULES },
  { type: 'matching_rule', id: 'matching.drata', name: 'Drata task-group matching', description: 'Maps Drata control names to outreach task groups.', body: DEFAULT_DRATA_MATCHING_RULES },
  ...DEFAULT_REPORT_COLUMNS.map((r) => ({ type: 'report_columns' as const, id: reportColumnsTemplateId(r.report), name: `Columns: ${r.report.replace(/_/g, ' ')}`, description: `Column definitions for the ${r.report.replace(/_/g, ' ')} report.`, body: r })),
  ...ALL_HELP_ARTICLES.map((a) => ({ type: 'help_content' as const, id: helpTemplateId(a.slug), name: a.title, description: a.summary, body: helpArticleBody(a) })),
  { type: 'model_config', id: 'config.model', name: 'Model configuration', description: 'Rumi model ids, output limits, budgets and the dated price table.', body: DEFAULT_MODEL_CONFIG },
  { type: 'rate_limit_config', id: 'config.rate_limits', name: 'Rate-limit configuration', description: 'Provider ceilings, auth buckets, egress groups and retry policy.', body: DEFAULT_RATE_LIMIT_CONFIG },
  { type: 'feature_flags', id: 'config.feature_flags', name: 'Feature flags', description: 'Feature toggles and per-account Rumi disablement.', body: DEFAULT_FEATURE_FLAGS },
];

export const DEFAULT_CONTENT_ITEMS: readonly ContentBundleItem[] = DEFAULT_CONTENT_ITEM_INPUTS.map((input) => makeBundleItem(input.type, input.id, 1, input.body));

export interface BuildContentBundleOptions {
  version?: string;
  channel?: ContentManifest['channel'];
  publishedAt?: string;
  publishedByUserId?: UserId;
  signature?: string;
  signingKeyId?: string;
  bundleUrl?: string;
  changeSummary?: string;
}

/** Assemble the default bundle. The server replaces `signature`/`signingKeyId` after signing. */
export function buildDefaultContentBundle(options: BuildContentBundleOptions = {}): ContentBundle {
  const items = DEFAULT_CONTENT_ITEMS.slice();
  const version = options.version ?? DEFAULT_CONTENT_VERSION;
  const publishedByUserId = options.publishedByUserId ?? (staffBySlug('amir-tarighat').userId as UserId);
  const bundleBytes = new TextEncoder().encode(canonicalJson(items)).length;
  const manifest: ContentManifest = {
    version,
    channel: options.channel ?? DEFAULT_CONTENT_CHANNEL,
    publishedAt: options.publishedAt ?? FIXTURE_NOW_ISO,
    publishedByUserId,
    digest: computeBundleDigest(items),
    signature: options.signature ?? 'unsigned',
    signingKeyId: options.signingKeyId ?? 'unsigned',
    bundleUrl: options.bundleUrl ?? `/v1/content/bundles/${version}`,
    bundleBytes,
    compatibleAppVersions: DEFAULT_CONTENT_COMPATIBLE_APP_VERSIONS,
    schemaVersion: CONTENT_SCHEMA_VERSION,
    items: items.map((i) => ({ type: i.type, id: i.id, version: i.version, digest: i.digest })),
    changeSummary: options.changeSummary ?? DEFAULT_CONTENT_CHANGE_SUMMARY,
  };
  return { manifest, items };
}

/** Unsigned default bundle for previews and tests. */
export const DEFAULT_CONTENT_BUNDLE: ContentBundle = buildDefaultContentBundle();

export function findContentItem<T = unknown>(type: TemplateType, id: string): (ContentBundleItem & { body: T }) | undefined {
  return DEFAULT_CONTENT_ITEMS.find((i) => i.type === type && i.id === id) as (ContentBundleItem & { body: T }) | undefined;
}

export function contentItemsOfType<T = unknown>(type: TemplateType): (ContentBundleItem & { body: T })[] {
  return DEFAULT_CONTENT_ITEMS.filter((i) => i.type === type) as (ContentBundleItem & { body: T })[];
}
